import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {SaveToResultsStepDataName} from 'analysis-tool/AnalysisStepDataNames';
import {ANALYSIS_TOOL_SPACING, ARCHIVED_ANALYSIS_TOOLTIP_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  GetSubsetDataFrame,
  GetSubsetDataFrameVariables,
  GetSubsetDataFrame_dataFrame_DataFrameNode as RowsWithErrorsDataFrame,
} from 'data-frames/graphql/__generated/GetSubsetDataFrame';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {DEFAULT_DATA_FRAME_ROW_LIMIT, GET_SUBSET_DATA_FRAME_QUERY} from 'data-frames/graphql/GetDataFrame';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {ResultsArchiveModal} from 'notebook/structured-tables/components/modals/ResultsArchiveModal';
import * as PipelineActions from 'pipelines/network/PipelineActions';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayResult} from 'results/assays/AssayRecordConstants';
import AssayResultsTable from 'runs/AssayResultsTable';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {OverflowButton} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

import RetryFailedSaveToResultsStepBanner from './RetryFailedSaveToResultsStepBanner';
import SavedResultsQuery from './SavedResultsQuery';

export const MAX_RESULT_ROWS = 500;
const ERROR_ROWS_BANNER_TEXT_SOME_ROWS_FAILED = 'The following rows were unable to be saved to Results.';
const ERROR_ROWS_BANNER_TEXT_ALL_ROWS_FAILED = 'No rows could be saved to Results.';
const ERROR_ROWS_BANNER_ADDITIONAL_TEXT = 'Please see error message in the last column';

export interface PresentationalSucceededDataMappingResultsProps {
  analysis: AnalysisGraphQL;
  numSavedResults: number;
  rowsWithErrorsDataFrame: RowsWithErrorsDataFrame;
  refetchResults: () => void;
  resultsToDisplay: Array<AssayResult>;
  resultSchema: ResultSchema;
  stepId: string;
  clientStateId: string;
  openReconfigurationView: () => void;
}

export function PresentationalSucceededDataMappingResults({
  analysis,
  numSavedResults,
  rowsWithErrorsDataFrame,
  refetchResults,
  resultsToDisplay,
  resultSchema,
  stepId,
  clientStateId,
  openReconfigurationView,
}: PresentationalSucceededDataMappingResultsProps): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const areAllQueriedResultsArchived = resultsToDisplay.every((result) => result.archiveRecord != null);
  const areAllQueriedResultsNotArchived = resultsToDisplay.every((result) => result.archiveRecord == null);

  function handleDownloadAsCSV(): void {
    PipelineActions.exportSavedResultsDataFrame(stepId);
  }

  async function unarchiveResults(): Promise<void> {
    await PipelineDAO.unarchiveSavedResultsFromPipelineStep(stepId);
    refetchResults();
  }

  async function archiveResults(purpose: string): Promise<void> {
    await PipelineDAO.archiveSavedResultsFromPipelineStep(stepId, purpose);
    refetchResults();
  }

  function updateArchiveStatusAndRefetch(): void {
    if (areAllQueriedResultsArchived) {
      withLoadingScreen(unarchiveResults(), 'Unarchiving results...');
      return;
    }

    runModal(ResultsArchiveModal, {
      onRequestSubmit: (archivePurpose) => {
        withLoadingScreen(archiveResults(archivePurpose), 'Archiving results...');
      },
      onRequestClose: () => {},
      numResults: numSavedResults,
    });
  }

  const optionProps = {
    onSelect: updateArchiveStatusAndRefetch,
    isDisabled: isArchived(analysis),
    tooltipTitle: isArchived(analysis) ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT : undefined,
  };

  const includeArchiveOptions = analysis.permissions.writable || analysis.permissions.unarchivable;

  const headerControls = [
    <Dropdown
      key="overflow-menu"
      closeOnInsideClick={true}
      closeOnOutsideClick={true}
      menu={
        <Menu dropLeft={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
          {includeArchiveOptions && areAllQueriedResultsNotArchived && (
            <Option {...optionProps}>Archive results</Option>
          )}
          {includeArchiveOptions && areAllQueriedResultsArchived && (
            <Option {...optionProps}>Unarchive results</Option>
          )}
          <Option
            onSelect={() => {
              exportAuditLogs([], {relatedResultsModelId: stepId});
            }}
            isDisabled={false}
            tooltipTitle={EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING}
          >
            {EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING}
          </Option>
        </Menu>
      }
      isOpen={isMenuOpen}
      onToggle={() => {
        setIsMenuOpen(!isMenuOpen);
      }}
    >
      <OverflowButton enableTooltip={true} className="u-mini-spacing-left" />
    </Dropdown>,
  ];

  return (
    <>
      {rowsWithErrorsDataFrame.numRows > 0 && (
        <div className="u-small-spacing-bottom">
          <DataFrameTableWrapper
            dataFrameId={rowsWithErrorsDataFrame.id}
            actionOrigin={ActionOrigin.ANALYSIS_TOOL}
            banner={
              <>
                {resultsToDisplay.length > 0 ? (
                  <InlineBanner
                    bannerType={InlineBannerType.WARNING}
                    text={ERROR_ROWS_BANNER_TEXT_SOME_ROWS_FAILED}
                    supportingInfo={ERROR_ROWS_BANNER_ADDITIONAL_TEXT}
                  />
                ) : (
                  <RetryFailedSaveToResultsStepBanner
                    openReconfigurationView={
                      analysis.permissions.writable ? openReconfigurationView : undefined
                    }
                    text={ERROR_ROWS_BANNER_TEXT_ALL_ROWS_FAILED}
                    supportingInfo={ERROR_ROWS_BANNER_ADDITIONAL_TEXT}
                  />
                )}
                <DataFrameWarningBanner
                  isRandomSubset={false}
                  name={rowsWithErrorsDataFrame.dataFrameJson.name}
                  renderedColumns={rowsWithErrorsDataFrame.dataFrameJson.columns.length}
                  renderedRows={rowsWithErrorsDataFrame.dataFrameJson.rows.length}
                  totalColumns={rowsWithErrorsDataFrame.allColumnsJson.length}
                  totalRows={rowsWithErrorsDataFrame.numRows}
                />
              </>
            }
            clientStateId={clientStateId}
            dataFrame={rowsWithErrorsDataFrame.dataFrameJson}
            renderIsExcludedColumn={false}
          />
        </div>
      )}
      {numSavedResults > resultsToDisplay.length && (
        <div className={css(styles.resultLimitInfoBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={`Showing the first ${MAX_RESULT_ROWS} Results of ${numSavedResults}.`}
            supportingInfo="Download to view the complete data."
          />
        </div>
      )}
      {resultsToDisplay.length > 0 && (
        <div
          className={classNames(
            'mediocre',
            'react-tab-container--mediocre-tableWrapper',
            'react-tab-container--mediocre-tableWrapper--noPaddingTop'
          )}
        >
          <div key={resultSchema.id} className="mediocre-item is-table">
            <AssayResultsTable
              additionalColumns={[]}
              attachedForReview={false}
              autoCollapseLimit={MAX_RESULT_ROWS}
              headerControls={headerControls}
              results={resultsToDisplay}
              schema={resultSchema}
              canRename={false}
              updateDisplayConfigHandler={() => {}}
              {...standaloneProps}
              fileId=""
              cid={clientStateId}
              isOversized={false}
              locked={true}
              onDownloadAsCSV={handleDownloadAsCSV}
            />
          </div>
        </div>
      )}
    </>
  );
}

export interface SucceededDataMappingResultProps {
  analysis: AnalysisGraphQL;
  step: GetAnalysisPipelineStep;
  clientStateId: string;
  openReconfigurationView: () => void;
}

export default function SucceededDataMappingResult({
  analysis,
  step,
  clientStateId,
  openReconfigurationView,
}: SucceededDataMappingResultProps): JSX.Element {
  const savedResultsDataFrameId = findAndAssertSingleStepDataFrameId(
    step,
    SaveToResultsStepDataName.SAVED_RESULTS
  );
  const rowsWithErrorsDataFrameId = findAndAssertSingleStepDataFrameId(
    step,
    SaveToResultsStepDataName.ROWS_WITH_ERRORS
  );

  return (
    <BenchlingQuery<GetSubsetDataFrame, GetSubsetDataFrameVariables>
      query={GET_SUBSET_DATA_FRAME_QUERY}
      variables={{
        dataFrameId: rowsWithErrorsDataFrameId,
        limit: DEFAULT_DATA_FRAME_ROW_LIMIT,
      }}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrame: rowsWithErrorsDataFrame}) => {
        assert(!isInaccessible(rowsWithErrorsDataFrame), 'User does not have access to step output');

        // TODO (BNCH-54762) Add pagination for result sets with more than `MAX_RESULT_ROWS` rows
        const {schemaId} = getPipelineStepConfig(step, PipelineStepType.SAVE_TO_RESULTS, 2);

        return (
          <SavedResultsQuery
            dataFrameId={savedResultsDataFrameId}
            rowLimit={MAX_RESULT_ROWS}
            resultSchemaId={schemaId}
          >
            {(totalNumSavedResults, results, resultSchema, refetchResults) => (
              <PresentationalSucceededDataMappingResults
                analysis={analysis}
                numSavedResults={totalNumSavedResults}
                rowsWithErrorsDataFrame={rowsWithErrorsDataFrame}
                refetchResults={refetchResults}
                resultsToDisplay={results}
                resultSchema={resultSchema}
                stepId={step.id}
                clientStateId={clientStateId}
                openReconfigurationView={openReconfigurationView}
              />
            )}
          </SavedResultsQuery>
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  errorRowsContainer: {
    paddingBottom: ANALYSIS_TOOL_SPACING,
  },
  resultLimitInfoBanner: {
    paddingBottom: ANALYSIS_TOOL_SPACING,
  },
});
